(() => {
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

  function getDrawerRoot() {
    return (
      document.querySelector("cart-drawer-component") ||
      document.querySelector("[data-cart-drawer]") ||
      document
    );
  }

  function getCheckoutButton(root) {
    return (
      root.querySelector('a[href*="/checkout"]') ||
      root.querySelector('button[name="checkout"]') ||
      root.querySelector("[data-checkout]") ||
      null
    );
  }

  function isTransparent(color) {
    if (!color) return true;
    const c = color.trim().toLowerCase();
    return c === "transparent" || c === "rgba(0, 0, 0, 0)" || c.endsWith(", 0)");
  }

  function applyCheckoutColors(fab, checkoutBtn) {
    if (!checkoutBtn) return;

    const cs = getComputedStyle(checkoutBtn);
    let bg = cs.backgroundColor;
    let fg = cs.color;

    const root = getComputedStyle(document.documentElement);
    if (isTransparent(bg)) {
      bg =
        root.getPropertyValue("--button-background")?.trim() ||
        root.getPropertyValue("--color-button")?.trim() ||
        bg;
    }
    if (!fg || isTransparent(fg)) {
      fg =
        root.getPropertyValue("--button-text")?.trim() ||
        root.getPropertyValue("--color-button-text")?.trim() ||
        fg;
    }

    if (bg && !isTransparent(bg)) fab.style.backgroundColor = bg;
    if (fg && !isTransparent(fg)) fab.style.color = fg;
  }

  function findScrollableAncestor(startEl, boundaryEl) {
    let node = startEl?.parentElement;
    while (node && node !== boundaryEl && node !== document.body) {
      const cs = getComputedStyle(node);
      const oy = cs.overflowY;
      if ((oy === "auto" || oy === "scroll") && node.scrollHeight > node.clientHeight + 10) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  function findScrollableInside(rootEl) {
    if (!rootEl || rootEl === document) return null;
    const all = rootEl.querySelectorAll("*");
    for (const el of all) {
      const cs = getComputedStyle(el);
      const oy = cs.overflowY;
      if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight + 10) {
        return el;
      }
    }
    return null;
  }

  function isElementVisibleInScroller(el, scroller) {
    if (!el || !scroller) return false;

    const elRect = el.getBoundingClientRect();
    const scRect = scroller.getBoundingClientRect();

    const visiblePx = Math.min(elRect.bottom, scRect.bottom) - Math.max(elRect.top, scRect.top);
    const ratio = visiblePx / Math.max(1, elRect.height);
    return ratio >= 0.4;
  }

  function scrollToCheckout(checkoutBtn, scroller) {
    if (!checkoutBtn || !scroller) return;

    const scRect = scroller.getBoundingClientRect();
    const targetRect = checkoutBtn.getBoundingClientRect();

    const currentTop = scroller.scrollTop;
    const delta = targetRect.top - scRect.top;
    const targetTop = currentTop + delta - 24;

    scroller.scrollTo({
      top: clamp(targetTop, 0, scroller.scrollHeight),
      behavior: "smooth",
    });
  }

  function initFab(fab) {
    if (!fab || fab.dataset.fabInited === "1") return;
    fab.dataset.fabInited = "1";

    const drawerRoot = getDrawerRoot();
    let raf = null;

    const update = () => {
      raf = null;

      const checkoutBtn = getCheckoutButton(drawerRoot);
      if (!checkoutBtn) {
        fab.classList.remove("is-visible");
        return;
      }

      applyCheckoutColors(fab, checkoutBtn);

      const scroller =
        findScrollableAncestor(checkoutBtn, drawerRoot) ||
        findScrollableInside(drawerRoot);

      if (!scroller) {
        fab.classList.remove("is-visible");
        return;
      }

      const visible = isElementVisibleInScroller(checkoutBtn, scroller);
      fab.classList.toggle("is-visible", !visible);

      fab._target = checkoutBtn;
      fab._scroller = scroller;
    };

    const scheduleUpdate = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };

    fab.addEventListener("click", () => {
      const target = fab._target || getCheckoutButton(drawerRoot);
      const scroller =
        fab._scroller ||
        findScrollableAncestor(target, drawerRoot) ||
        findScrollableInside(drawerRoot);

      if (!target || !scroller) return;
      scrollToCheckout(target, scroller);
    });

    // Aggiorna su eventi carrello
    document.addEventListener("cart:update", scheduleUpdate);
    document.addEventListener("cart:refresh", scheduleUpdate);

    // Aggiorna quando DOM del drawer cambia (totals / cross sell / ecc.)
    const moRoot = drawerRoot === document ? document.documentElement : drawerRoot;
    const mo = new MutationObserver(() => {
      scheduleUpdate();
      setTimeout(scheduleUpdate, 50);
    });
    mo.observe(moRoot, { childList: true, subtree: true });

    // Aggiorna su resize
    window.addEventListener("resize", scheduleUpdate);

    // Aggiorna su add-to-cart submit/click (copre AJAX senza eventi)
    document.addEventListener(
      "submit",
      (e) => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;
        const action = (form.getAttribute("action") || "").toLowerCase();
        if (!action.includes("/cart/add")) return;

        // sequenza update: DOM + layout
        scheduleUpdate();
        setTimeout(scheduleUpdate, 120);
        setTimeout(scheduleUpdate, 300);
        setTimeout(scheduleUpdate, 700);
      },
      true
    );

    document.addEventListener(
      "click",
      (e) => {
        const btn = e.target?.closest?.('button[name="add"], button[type="submit"], [data-add-to-cart]');
        if (!btn) return;
        scheduleUpdate();
        setTimeout(scheduleUpdate, 120);
        setTimeout(scheduleUpdate, 300);
      },
      true
    );

    // Patch open() se esiste (alcuni temi aprono drawer via custom element)
    if (window.customElements && customElements.get("cart-drawer-component")) {
      const proto = customElements.get("cart-drawer-component").prototype;
      if (proto && !proto.__fabPatchedFinal2) {
        proto.__fabPatchedFinal2 = true;
        const originalOpen = proto.open;
        if (typeof originalOpen === "function") {
          proto.open = function (...args) {
            const out = originalOpen.apply(this, args);
            setTimeout(update, 0);
            setTimeout(update, 50);
            setTimeout(update, 150);
            return out;
          };
        }
      }
    }

    // IMPORTANT: update immediato + un paio di tick dopo (drawer appena iniettato)
    update();
    setTimeout(update, 50);
    setTimeout(update, 150);
  }

  function boot() {
    // Inizializza tutte le fab presenti (anche se aggiunte dopo)
    document.querySelectorAll("[data-checkout-fab]").forEach(initFab);
  }

  // Boot immediato
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
  document.addEventListener("shopify:section:load", boot);

  // ✅ CHIAVE: osserva la comparsa della FAB (drawer creato dopo add-to-cart)
  const globalObserver = new MutationObserver(() => boot());
  globalObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
